import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { RootState } from '@next/core-lib/app';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { TableColumnTypes, TableConfig } from '@next/core-lib/table';
import { Store } from '@ngrx/store';
import { map, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import {
  Flock,
  CarbonFootprintResponse,
  Impact,
  ImpactRow,
  StageImpact,
  FarmResource,
} from 'src/app/flock/models/flock.model';
import {
  getCarbonFootprint,
  getFarmResources,
  getFlocks,
  getSelectedFlockId,
  selectSustainabilityNoticeVisibility,
} from 'src/app/flock/store/selectors/flock.selectors';

import * as flockActions from '../../../store/actions/flock.actions';

@Component({
  selector: 'app-carbon-footprint',
  templateUrl: './carbon-footprint.component.html',
  styleUrls: ['./carbon-footprint.component.scss'],
  standalone: false,
})
export class CarbonFootprintComponent implements OnInit, OnDestroy {
  @ViewChild('resourceColumn') resourceColumn: TemplateRef<unknown>;

  private readonly destroyed$ = new Subject<void>();
  private subscription: Subscription;

  public showNotification = true;

  flocks$: Observable<Flock[]> = this.store
    .select(getFlocks)
    .pipe(map((flocks) => flocks.filter((f) => !!f.departure?.departureDate)));
  selectedFlockId$: Observable<number> = this.store.select(getSelectedFlockId);
  carbonFootprint$: Observable<CarbonFootprintResponse> = this.store.select(getCarbonFootprint);
  farmResources$: Observable<FarmResource[]> = this.store.select(getFarmResources);
  selectedFlockId: number = null;
  selectedFlock: Flock = null;
  farmResources: FarmResource[] = [];

  tableConfig: TableConfig = {
    dataSource: new MatTableDataSource<Impact>([]),
    columns: [],
  };
  carbonFootprintErrors: string[] = [];
  stagesImpact: StageImpact[] = [];
  sustellLogoSrc = 'assets/images/sustell_logo.png';

  constructor(
    private readonly store: Store<RootState>,
    private readonly locale: LocaleService,
    private readonly dialogService: DialogService,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(flockActions.loadFarmResourcesRequest());

    this.tableConfig = {
      ...this.tableConfig,
      pagination: false,
      selectColumn: false,
      actionColumn: false,
      scrolling: true,
      disableCards: true,
      wrapperWidth: '100%',
      columns: [
        {
          id: 'name',
          type: TableColumnTypes.STRING,
          label: '',
          disableFormatting: true,
          className: 'text--bold',
        },
        {
          id: 'Consumption egg',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.consumptionEgg'),
          digitsInfo: '1.03-03',
        },
        {
          id: 'Spent laying hen',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sustainability.spentHens'),
          digitsInfo: '1.03-03',
        },
      ],
      actions: [],
    };

    this.carbonFootprint$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: CarbonFootprintResponse) => {
        this.stagesImpact =
          response?.results?.[0]?.carbonFootprintResult?.footprint?.stagesImpact ?? [];
        const errors = response?.results?.[0]?.carbonFootprintResult?.footprint?.errors ?? [];

        this.carbonFootprintErrors = errors
          .filter((error) => error.key === 'error.0.errorInfo.0.externalMsg')
          .flatMap((error) => error.messages);

        const impactRows: Record<string, ImpactRow> = {};

        for (const stage of this.stagesImpact) {
          const animalType = stage.animalType;

          for (const impact of stage.impacts) {
            const impactName = impact.name;

            if (!impactRows[impactName]) {
              impactRows[impactName] = { name: impactName };
            }

            impactRows[impactName][animalType] = impact.value;
          }
        }

        const dataForTable = Object.values(impactRows);

        this.tableConfig = {
          ...this.tableConfig,
          dataSource: new MatTableDataSource(dataForTable),
        };
      });

    this.subscription = this.store
      .select(selectSustainabilityNoticeVisibility)
      .subscribe((visible) => (this.showNotification = visible));

    this.farmResources$.pipe(takeUntil(this.destroyed$)).subscribe((farmResources) => {
      this.farmResources = farmResources;
    });
  }

  onFlockSelected(flock: Flock): void {
    this.selectedFlockId = flock.id;
    this.selectedFlock = flock;

    this.store.dispatch(
      flockActions.setSelectedFlockId({ id: flock.id, save: true, reloadWeekData: false }),
    );
    this.store.dispatch(flockActions.loadCarbonFootprintRequest({ flockId: flock.id }));
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.store.dispatch(flockActions.resetCarbonFootprint());

    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  public getActionText() {
    return this.showNotification
      ? this.locale.translate('sustainability.action-text-close')
      : this.locale.translate('sustainability.action-text-open');
  }

  public toggleNotification() {
    this.store.dispatch(
      flockActions.setSustainabilityNoticeVisibility({ visible: !this.showNotification }),
    );
  }

  startCalculateCarbonFootprint(): void {
    const validationMessages = this.validateFlockForCarbonFootprintCalculation();

    if (validationMessages.length > 0) {
      this.dialogService.confirmWarning({
        title: this.locale.translate('sustainability.carbon-footprint-title'),
        text: this.getValidationHtmlMessage(validationMessages),
      });

      return;
    }

    this.dialogService
      .confirmWarning({
        title: this.locale.translate('sustainability.carbon-footprint-title'),
        text: this.locale.translate('sustainability.carbon-footprint-text'),
      })
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          this.store.dispatch(
            flockActions.calculateCarbonFootprintRequest({ flockId: this.selectedFlockId }),
          );
        }
      });
  }

  refreshCarbonFootprintResult(): void {
    this.store.dispatch(
      flockActions.refreshCarbonFootprintRequest({ flockId: this.selectedFlockId }),
    );
  }

  validateFlockForCarbonFootprintCalculation(): string[] {
    const validationMessages: string[] = [];

    const arrivalDate = this.selectedFlock.arrival?.arrivalDate
      ? new Date(this.selectedFlock.arrival.arrivalDate)
      : null;
    const departureDate = this.selectedFlock.departure?.departureDate
      ? new Date(this.selectedFlock.departure.departureDate)
      : null;

    const flockYears = this.getYearsBetweenDates(arrivalDate, departureDate);

    const farmResourceYears = (this.farmResources || []).map((res) => res.year);
    const allYearsCovered = flockYears.every((year) => farmResourceYears.includes(year));

    if (!allYearsCovered) {
      validationMessages.push(this.locale.translate('sustainability.farm-resources-not-complete'));
    }

    if (!this.selectedFlock.arrival?.beddingType) {
      validationMessages.push(this.locale.translate('sustainability.bedding-type-required'));
    } else if (
      this.selectedFlock.arrival.beddingType !== 'NoBedding' &&
      (this.selectedFlock.arrival.beddingAmount === null ||
        this.selectedFlock.arrival.beddingAmount === undefined)
    ) {
      validationMessages.push(this.locale.translate('sustainability.bedding-amount-required'));
    }

    if (this.selectedFlock.arrival?.henWeightInGrams == null) {
      validationMessages.push(this.locale.translate('sustainability.hen-weight-required'));
    }

    if (
      !this.selectedFlock.weekRegistrations ||
      this.selectedFlock.weekRegistrations.length === 0
    ) {
      validationMessages.push(this.locale.translate('sustainability.week-registrations-required'));
    } else {
      const hasInvalidWeekRegistrations = this.selectedFlock.weekRegistrations.some((weekReg) => {
        return (
          !weekReg || weekReg.registrationData === null || weekReg.registrationData === undefined
        );
      });

      if (hasInvalidWeekRegistrations) {
        validationMessages.push(
          this.locale.translate('sustainability.week-registrations-incomplete'),
        );
      }

      const hasMissingDayRegistrations = this.selectedFlock.weekRegistrations.some((weekReg) => {
        if (!weekReg || !weekReg.dayRegistrations) {
          return true;
        }
        return weekReg.dayRegistrations.some((dayReg) => {
          return (
            !dayReg || dayReg.registrationData === null || dayReg.registrationData === undefined
          );
        });
      });

      if (hasMissingDayRegistrations) {
        validationMessages.push(this.locale.translate('sustainability.day-registrations-missing'));
      }
    }

    return validationMessages;
  }

  getYearsBetweenDates(arrivalDate: Date | null, departureDate: Date | null): number[] {
    const years: number[] = [];

    if (!arrivalDate || !departureDate || departureDate.getFullYear() < arrivalDate.getFullYear()) {
      return years;
    }

    for (let year = arrivalDate.getFullYear(); year <= departureDate.getFullYear(); year++) {
      years.push(year);
    }

    return years;
  }

  getValidationHtmlMessage(validationMessages: string[]): string {
    return validationMessages.length > 0
      ? '<ul>' + validationMessages.map((msg) => `<li>${msg}</li>`).join('') + '</ul>'
      : '';
  }
}
